
import { Component, Prop, Vue } from 'vue-property-decorator'
import KAMReadOnlyForm from '@/views/kam/forms/KAMReadOnlyForm.vue'
import { dateLabel, personLabel, ReadOnlySection, valueOrDash } from '@/views/kam/forms/kamReadOnlyPresenter'

type AssessmentKey = 'organization' | 'documents' | 'riskInventory' | 'instruction' | 'siteControl' | 'emergency' | 'incidents' | 'subcontractors'

@Component({
  components: { 'kam-read-only-form': KAMReadOnlyForm }
})
export default class KAM004V1 extends Vue {
  @Prop({ required: true }) answers!: Record<string, any>
  @Prop({ default: () => ({}) }) media!: Record<string, any>
  @Prop({ default: () => [] }) siteManagers!: any[]
  @Prop({ default: () => [] }) projectLeaders!: any[]
  @Prop({ default: () => [] }) constructionWorkers!: any[]
  @Prop({ default: () => [] }) employees!: any[]
  @Prop({ default: () => [] }) qualityManagement!: any[]

  assessmentLabels: Record<AssessmentKey, string> = {
    organization: 'Organisatie en verantwoordelijkheden',
    documents: 'VGM-plan en projectdocumentatie',
    riskInventory: 'Risico-inventarisatie en beheersmaatregelen',
    instruction: 'Voorlichting en instructie',
    siteControl: 'Bouwplaatsinrichting en toezicht',
    emergency: 'Noodorganisatie en middelen',
    incidents: 'Incidenten en afwijkingen',
    subcontractors: 'Onderaannemers en leveranciers'
  }

  scoreLabels: Record<string, string> = {
    voldoende: 'Voldoende',
    aandachtspunt: 'Aandachtspunt',
    onvoldoende: 'Onvoldoende',
    nvt: 'N.v.t.'
  }

  scoreColors: Record<string, string> = {
    voldoende: 'green',
    aandachtspunt: 'orange',
    onvoldoende: 'red',
    nvt: 'default'
  }

  judgementLabels: Record<string, string> = {
    voldoende: 'Voldoende',
    onvoldoende: 'Onvoldoende'
  }

  get sections () : ReadOnlySection[] {
    const answers = this.answers || {}
    return [
      {
        key: 'context',
        title: 'Project- en beoordelingscontext',
        fields: [
          { key: 'projectName', label: 'Projectnaam', value: valueOrDash(answers.projectName) },
          { key: 'projectReference', label: 'Projectnummer', value: valueOrDash(answers.projectReference) },
          { key: 'projectLeaderId', label: 'Projectleider', value: personLabel(answers.projectLeaderId, this.projectLeaders) },
          { key: 'siteManagerId', label: 'Uitvoerder', value: personLabel(answers.siteManagerId, this.siteManagers) },
          { key: 'assessorId', label: 'Beoordelaar', value: this.kamManagerLabel(answers.assessorId, answers.assessorName) },
          { key: 'assessmentDate', label: 'Datum beoordeling', value: dateLabel(answers.assessmentDate) },
          { key: 'scope', label: 'Scope / omschrijving', value: valueOrDash(answers.scope) }
        ]
      },
      {
        key: 'assessments',
        title: 'VGM-beheersing',
        tables: [
          {
            key: 'assessments',
            label: 'Beoordelingsonderdelen',
            rows: this.assessmentRows,
            columns: [
              { key: 'title', label: 'Onderdeel' },
              { key: 'score', label: 'Beoordeling' },
              { key: 'finding', label: 'Bevinding / toelichting' },
              { key: 'action', label: 'Actie / maatregel' }
            ]
          }
        ]
      },
      {
        key: 'actions',
        title: 'Actiepunten',
        tables: [
          {
            key: 'actions',
            label: 'Actiepunten',
            rows: Array.isArray(answers.actions) ? answers.actions : [],
            columns: [
              { key: 'description', label: 'Actiepunt' },
              { key: 'owner', label: 'Eigenaar' },
              { key: 'dueDate', label: 'Termijn' },
              { key: 'done', label: 'Gereed' }
            ]
          }
        ]
      },
      {
        key: 'final',
        title: 'Afronding',
        fields: [
          { key: 'finalJudgement', label: 'Eindoordeel', value: this.judgementLabels[answers.finalJudgement] || valueOrDash(answers.finalJudgement) },
          { key: 'followUpOwnerId', label: 'Opvolging door', value: this.kamManagerLabel(answers.followUpOwnerId, answers.followUpOwner) },
          { key: 'followUpDate', label: 'Opvolgdatum', value: dateLabel(answers.followUpDate) },
          { key: 'finalNotes', label: 'Algemene toelichting', value: valueOrDash(answers.finalNotes) }
        ]
      }
    ]
  }

  get assessmentRows () : Array<Record<string, any>> {
    const assessments = this.answers?.assessments || {}
    return Object.keys(this.assessmentLabels).map((key) => {
      const row = assessments[key] || {}
      const score = row.score || ''
      return {
        title: this.assessmentLabels[key as AssessmentKey],
        score: score
          ? { kind: 'tag', label: this.scoreLabels[score] || valueOrDash(score), color: this.scoreColors[score] || 'default', className: `kam004-score-tag kam004-score-tag-${score}` }
          : { kind: 'tag', label: '-', color: 'default', className: 'kam004-score-tag kam004-score-tag-empty' },
        finding: valueOrDash(row.finding),
        action: valueOrDash(row.action)
      }
    })
  }

  kamManagerLabel (id: unknown, snapshot: unknown) : string {
    if (id !== null && typeof id !== 'undefined' && id !== '') {
      const person = this.qualityManagement.find(option => String(option.id ?? option.value) === String(id))
      if (person) return person.label || person.full_name || person.name || String(id)
    }

    return valueOrDash(snapshot)
  }
}
